import { FILE_NAME_COLLATOR, toEpochMs } from 'app/utils/task-hist-sort.utils';

/**
 * Orders of the Tasks folder of an agroDrive device.
 * `fetch-agrodrive` returns every task of the device at once, so the list is
 * searched and sorted on the client. The agroDrive index only stores the file
 * name, the processing status and the completion date of a task, so the work
 * date and the upload date of the task report are not available here.
 */
export enum AgrodriveTaskSortOrder {
  /** Completion date (`metadata.completedAt`), newest first. The default. */
  CompletionDate = 'completionDate',
  /** File name, A to Z, case-insensitive. */
  FileName = 'fileName',
}

export const DEFAULT_AGRODRIVE_TASK_SORT_ORDER =
  AgrodriveTaskSortOrder.CompletionDate;

export const AGRODRIVE_TASK_SORT_OPTIONS: ReadonlyArray<{
  value: AgrodriveTaskSortOrder;
  labelKey: string;
}> = [
  {
    value: AgrodriveTaskSortOrder.CompletionDate,
    labelKey: 'agrodrive.sort-completion-date',
  },
  {
    value: AgrodriveTaskSortOrder.FileName,
    labelKey: 'archive.sort-file-name',
  },
];

export type SortableAgrodriveTask = {
  fileName?: string;
  metadata?: { completedAt?: string };
};

/**
 * Compares two agroDrive tasks for the given order.
 *
 * @remarks
 * `completedAt` is written only when the processing of the task ends, and it is
 * removed when the file is uploaded again. A task without a valid `completedAt`
 * is therefore still processing: it is the latest upload, so it goes first in
 * the completion date order. Ties fall back to the file name.
 */
export function compareAgrodriveTasks(
  a: SortableAgrodriveTask,
  b: SortableAgrodriveTask,
  sortOrder: AgrodriveTaskSortOrder,
): number {
  return compareSortKeys(toSortKey(a), toSortKey(b), sortOrder);
}

/**
 * Returns the tasks whose file name contains the search query, ignoring case
 * and surrounding spaces, in the given order. A blank query keeps every task.
 * The input array is not changed. Each completion date is parsed once per task,
 * not once per comparison.
 */
export function filterAndSortAgrodriveTasks<T extends SortableAgrodriveTask>(
  tasks: T[],
  searchQuery: string,
  sortOrder: AgrodriveTaskSortOrder,
): T[] {
  const query = (searchQuery ?? '').trim().toLocaleLowerCase();
  let result = [...tasks];
  if (query) {
    result = result.filter((task) =>
      (task.fileName ?? '').toLocaleLowerCase().includes(query),
    );
  }
  return result
    .map((task) => ({ task, key: toSortKey(task) }))
    .sort((a, b) => compareSortKeys(a.key, b.key, sortOrder))
    .map(({ task }) => task);
}

type SortKey = {
  fileName: string;
  completedAt: number | undefined;
};

function toSortKey(task: SortableAgrodriveTask): SortKey {
  return {
    fileName: task.fileName ?? '',
    completedAt: toEpochMs(task.metadata?.completedAt),
  };
}

function compareSortKeys(
  a: SortKey,
  b: SortKey,
  sortOrder: AgrodriveTaskSortOrder,
): number {
  if (sortOrder === AgrodriveTaskSortOrder.CompletionDate) {
    const result = compareCompletionDates(a.completedAt, b.completedAt);
    if (result !== 0) {
      return result;
    }
  }
  return FILE_NAME_COLLATOR.compare(a.fileName, b.fileName);
}

function compareCompletionDates(
  aCompletedAt: number | undefined,
  bCompletedAt: number | undefined,
): number {
  if (aCompletedAt === undefined && bCompletedAt === undefined) {
    return 0;
  }
  if (aCompletedAt === undefined) {
    return -1;
  }
  if (bCompletedAt === undefined) {
    return 1;
  }
  return bCompletedAt - aCompletedAt;
}
